import { useEffect, useState } from "react";
import { Link, Redirect } from "wouter";
import {
  ChevronLeft,
  Shield,
  ShieldOff,
  User as UserIcon,
  Search,
  Mic,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  useAdminUsers,
  useAppSettings,
  useMe,
  usePatchAppSettings,
  useSetPlatformAdmin,
  DEFAULT_COMMENTARY_VOICE,
  type AdminUserRow,
  type CommentaryVoiceSettings,
} from "@/lib/queries";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/cn";

export default function PlatformAdmin() {
  const { data: me, isLoading: meLoading } = useMe();
  const [search, setSearch] = useState("");
  const { data: users = [], isLoading } = useAdminUsers(search);

  if (meLoading) {
    return <div className="p-8 text-muted-foreground text-sm">Loading…</div>;
  }
  if (!me) return <Redirect to="/sign-in" />;
  if (!me.isPlatformAdmin) {
    return (
      <div className="p-8 max-w-md mx-auto text-center space-y-3">
        <Shield className="mx-auto text-muted-foreground" size={32} />
        <h1 className="text-2xl font-display uppercase tracking-tight">
          Restricted
        </h1>
        <p className="text-sm text-muted-foreground">
          You aren't a platform admin. If you should be, ask another platform
          admin to add you.
        </p>
        <Button asChild>
          <Link href="/dashboard">Back to dashboard</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-4xl mx-auto space-y-6">
      <header className="space-y-1">
        <div>
          <Link
            href="/dashboard"
            className="text-xs font-display uppercase tracking-widest text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            <ChevronLeft size={12} aria-hidden /> Dashboard
          </Link>
        </div>
        <h1 className="text-2xl sm:text-3xl font-display uppercase tracking-tight inline-flex items-center gap-2">
          <Shield className="text-primary" /> Platform admin
        </h1>
        <p className="text-muted-foreground text-sm">
          Global controls for the Cornholio platform. Use sparingly.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <CardDescription>
            Every signed-up user across all orgs. Toggle platform-admin to
            grant or revoke global access.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, @-slug, or email"
              className="pl-9"
            />
          </div>

          {isLoading ? (
            <p className="text-muted-foreground text-sm">Loading…</p>
          ) : users.length === 0 ? (
            <p className="text-muted-foreground text-sm py-6 text-center">
              No users match.
            </p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {users.map((u) => (
                <UserRow key={u.id} user={u} isYou={u.id === me.id} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <CommentaryVoiceCard />

      <Card className="border-muted">
        <CardHeader>
          <CardTitle>Other tools</CardTitle>
          <CardDescription>Tied to the same platform-admin role.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <Link
            href="/architecture"
            className="flex items-center justify-between p-3 rounded-md border border-border hover:bg-muted/40"
          >
            <span>
              <span className="font-medium">Architecture overview</span>
              <span className="block text-xs text-muted-foreground">
                Live dev tasks + system map.
              </span>
            </span>
            <span className="text-muted-foreground">→</span>
          </Link>
          <Link
            href="/admin/physics"
            className="flex items-center justify-between p-3 rounded-md border border-border hover:bg-muted/40"
          >
            <span>
              <span className="font-medium">Physics tuner</span>
              <span className="block text-xs text-muted-foreground">
                Live sliders for virtual-cornhole physics + Ghost AI.
              </span>
            </span>
            <span className="text-muted-foreground">→</span>
          </Link>
          <Link
            href="/admin/design"
            className="flex items-center justify-between p-3 rounded-md border border-border hover:bg-muted/40"
          >
            <span>
              <span className="font-medium">Asset designer</span>
              <span className="block text-xs text-muted-foreground">
                Generate, catalog, and activate scenes, boards, and bags
                (Gemini + R2).
              </span>
            </span>
            <span className="text-muted-foreground">→</span>
          </Link>
          <div className="p-3 rounded-md border border-border text-muted-foreground">
            Integration test runner —{" "}
            <code className="bg-muted px-1 py-0.5 rounded">
              GET /api/admin/tests
            </code>{" "}
            +{" "}
            <code className="bg-muted px-1 py-0.5 rounded">
              POST /api/admin/tests/run
            </code>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Global commentary voice — ElevenLabs voice id, DB-overrides-env,
// applies immediately (no redeploy).
function CommentaryVoiceCard() {
  const { data: settings } = useAppSettings(true);
  const patch = usePatchAppSettings();
  const [voice, setVoice] = useState("");
  const [vs, setVs] = useState<Required<CommentaryVoiceSettings>>(
    DEFAULT_COMMENTARY_VOICE,
  );
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (settings) {
      setVoice(settings.commentaryVoiceId ?? "");
      setVs({ ...DEFAULT_COMMENTARY_VOICE, ...settings.commentaryVoice });
    }
  }, [settings]);

  const savedVoice = settings?.commentaryVoiceId ?? "";
  const savedVs = { ...DEFAULT_COMMENTARY_VOICE, ...settings?.commentaryVoice };
  const dirty =
    settings != null &&
    (voice.trim() !== savedVoice ||
      (Object.keys(DEFAULT_COMMENTARY_VOICE) as Array<
        keyof CommentaryVoiceSettings
      >).some((k) => vs[k] !== savedVs[k]));
  const isDefaults = (
    Object.keys(DEFAULT_COMMENTARY_VOICE) as Array<keyof CommentaryVoiceSettings>
  ).every((k) => vs[k] === DEFAULT_COMMENTARY_VOICE[k]);

  const set = (k: keyof CommentaryVoiceSettings, v: number | boolean) => {
    setVs((p) => ({ ...p, [k]: v }));
    setSaved(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2">
          <Mic size={16} className="text-primary" /> Commentary voice
        </CardTitle>
        <CardDescription>
          ElevenLabs voice + delivery for the live AI announcer. Overrides the{" "}
          <code className="bg-muted px-1 py-0.5 rounded">ELEVENLABS_VOICE_ID</code>{" "}
          env — changes apply immediately, no redeploy.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">
            Voice ID
          </label>
          <Input
            value={voice}
            onChange={(e) => {
              setVoice(e.target.value);
              setSaved(false);
            }}
            placeholder="e.g. aOcS60CY8CoaVaZfqqb5"
            className="font-mono text-sm"
          />
          <p className="text-[11px] text-muted-foreground">
            Find ids in your ElevenLabs Voice Lab (a voice → ⋯ → Copy voice ID).
          </p>
        </div>

        <div className="space-y-3 border-t border-border pt-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-display uppercase tracking-widest text-muted-foreground">
              Delivery
            </span>
            <button
              type="button"
              disabled={isDefaults}
              onClick={() => {
                setVs(DEFAULT_COMMENTARY_VOICE);
                setSaved(false);
              }}
              className="text-[11px] text-primary disabled:text-muted-foreground/40 disabled:cursor-default hover:underline"
            >
              Reset to defaults
            </button>
          </div>
          <VoiceSlider
            label="Speed"
            value={vs.speed}
            min={0.7}
            max={1.2}
            step={0.05}
            onChange={(n) => set("speed", n)}
            help="0.7 slower · 1.0 natural · 1.2 faster"
          />
          <VoiceSlider
            label="Stability"
            value={vs.stability}
            min={0}
            max={1}
            step={0.05}
            onChange={(n) => set("stability", n)}
            help="Low = more expressive & variable · high = flatter & consistent"
          />
          <VoiceSlider
            label="Similarity"
            value={vs.similarityBoost}
            min={0}
            max={1}
            step={0.05}
            onChange={(n) => set("similarityBoost", n)}
            help="How closely it hugs the source voice"
          />
          <VoiceSlider
            label="Style"
            value={vs.style}
            min={0}
            max={1}
            step={0.05}
            onChange={(n) => set("style", n)}
            help="Style exaggeration — higher is more theatrical"
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={vs.speakerBoost}
              onChange={(e) => set("speakerBoost", e.target.checked)}
              className="h-4 w-4 accent-primary"
            />
            <span>Speaker boost</span>
            <span className="text-[11px] text-muted-foreground">
              extra clarity, slightly higher latency
            </span>
          </label>
        </div>

        <div className="flex items-center gap-3">
          <Button
            disabled={!dirty || patch.isPending}
            onClick={async () => {
              try {
                await patch.mutateAsync({
                  commentaryVoiceId: voice.trim(),
                  commentaryVoice: vs,
                });
                setSaved(true);
              } catch {
                /* surfaced by the disabled/loading state */
              }
            }}
          >
            {patch.isPending ? "Saving…" : "Save"}
          </Button>
          {saved && !dirty ? (
            <p className="text-xs text-muted-foreground">
              Saved — the announcer uses this now.
            </p>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function VoiceSlider({
  label,
  value,
  min,
  max,
  step,
  onChange,
  help,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (n: number) => void;
  help?: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-sm">{label}</label>
        <span className="text-xs text-muted-foreground tabular-nums">
          {value.toFixed(2)}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-primary"
      />
      {help ? <p className="text-[11px] text-muted-foreground">{help}</p> : null}
    </div>
  );
}

function UserRow({ user, isYou }: { user: AdminUserRow; isYou: boolean }) {
  const toggle = useSetPlatformAdmin();
  const [error, setError] = useState<string | null>(null);

  const initials = user.displayName
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  async function flip() {
    setError(null);
    try {
      await toggle.mutateAsync({
        userId: user.id,
        isPlatformAdmin: !user.isPlatformAdmin,
      });
    } catch (e) {
      setError((e as ApiError).message ?? "Could not update");
    }
  }

  return (
    <li className="flex items-center gap-3 p-3">
      <div className="h-10 w-10 rounded-full bg-muted overflow-hidden border border-border flex items-center justify-center text-sm font-display">
        {user.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt={user.displayName}
            className="h-full w-full object-cover"
          />
        ) : (
          initials || <UserIcon size={16} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium truncate">{user.displayName}</span>
          {isYou && (
            <span className="text-[10px] font-display uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/10 text-primary">
              you
            </span>
          )}
          {user.isPlatformAdmin && (
            <span className="text-[10px] font-display uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary text-primary-foreground inline-flex items-center gap-1">
              <Shield size={10} /> admin
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          @{user.slug}
          {user.email ? ` · ${user.email}` : ""}
          {" · "}
          {user.orgCount} org{user.orgCount === 1 ? "" : "s"}
        </div>
        {error && <div className="text-xs text-destructive mt-1">{error}</div>}
      </div>
      <Button
        variant={user.isPlatformAdmin ? "outline" : "default"}
        size="sm"
        onClick={flip}
        disabled={toggle.isPending || isYou}
        className={cn(isYou && "opacity-50")}
        title={
          isYou
            ? "Another platform admin must demote you."
            : user.isPlatformAdmin
              ? "Revoke platform admin"
              : "Grant platform admin"
        }
      >
        {user.isPlatformAdmin ? (
          <>
            <ShieldOff size={14} /> Revoke
          </>
        ) : (
          <>
            <Shield size={14} /> Grant
          </>
        )}
      </Button>
    </li>
  );
}
